import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getLastViewedTopicId } from '@/features/topics/state/useActiveTopic'
import {
  getTopicDetail,
  addKeywordsToList,
  updateTopicList,
} from '@/features/topics/api/topicsClient'

export function useSettingsQueriesController() {
  const queryClient = useQueryClient()
  const topicId = getLastViewedTopicId()

  const { data: topic, isLoading } = useQuery({
    queryKey: ['topics', 'detail', topicId],
    queryFn: ({ signal }) => getTopicDetail(topicId!, signal),
    enabled: topicId != null,
  })

  const [inputValue, setInputValue] = useState('')

  const addMutation = useMutation({
    mutationFn: (keywords: string[]) => addKeywordsToList(topicId!, keywords),
    onSuccess: ({ list }) => {
      queryClient.setQueryData(['topics', 'detail', topicId], list)
      queryClient.invalidateQueries({ queryKey: ['topics'] })
      setInputValue('')
    },
  })

  const removeMutation = useMutation({
    mutationFn: (keywords: string[]) => updateTopicList({ id: topicId!, keywords }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['topics', 'detail', topicId], updated)
      queryClient.invalidateQueries({ queryKey: ['topics'] })
    },
  })

  function submitAdd() {
    const trimmed = inputValue.trim()
    if (!trimmed || !topicId) return
    addMutation.mutate([trimmed])
  }

  function removeKeyword(keyword: string) {
    if (!topic || !topicId) return
    const next = topic.keywords.filter((k) => k !== keyword)
    removeMutation.mutate(next)
  }

  return {
    topicId,
    topic,
    isLoading,
    inputValue,
    setInputValue,
    submitAdd,
    removeKeyword,
    isAdding: addMutation.isPending,
    isRemoving: removeMutation.isPending,
  }
}
